import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FaCompass } from 'react-icons/fa';

import { useWorkspace } from '../contexts/WorkspaceContext';

const TourGuide = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { activeWorkspaceId } = useWorkspace();
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [branch, setBranch] = useState(() => {
    return localStorage.getItem('robridge_tour_branch') || null;
  });

  // Initialize tour step from localStorage
  useEffect(() => {
    const activeStep = localStorage.getItem('robridge_tour_step');
    if (activeStep) {
      setStep(parseInt(activeStep, 10));
    }

    // Attach global function to start/restart the tour
    window.startRoBridgeTour = () => {
      setStep(1);
      setBranch(null);
      localStorage.setItem('robridge_tour_step', '1');
      localStorage.removeItem('robridge_tour_branch');
      if (activeWorkspaceId) {
        localStorage.removeItem(`robridge_tour_completed_${activeWorkspaceId}`);
      }
    };

    window.startZonePutAwayTour = () => {
      setStep(20);
      setBranch('zone');
      localStorage.setItem('robridge_tour_step', '20');
      localStorage.setItem('robridge_tour_branch', 'zone');
    };

    return () => {
      delete window.startRoBridgeTour;
      delete window.startZonePutAwayTour;
    };
  }, [activeWorkspaceId]);

  // Check if tour should auto-trigger
  useEffect(() => {
    console.log('🔍 TourGuide Auto-trigger Hook called:', {
      activeWorkspaceId,
      step,
      tourCompleted: activeWorkspaceId ? localStorage.getItem(`robridge_tour_completed_${activeWorkspaceId}`) : null,
      shouldAutoStart: localStorage.getItem('robridge_tour_should_auto_start')
    });

    if (!activeWorkspaceId) return;

    const tourCompleted = localStorage.getItem(`robridge_tour_completed_${activeWorkspaceId}`);
    const shouldAutoStart = localStorage.getItem('robridge_tour_should_auto_start');

    if (!tourCompleted && shouldAutoStart === 'true') {
      console.log('🚀 Tour Guide conditions met! Starting tour...');
      // Consume the flag so it doesn't trigger again on subsequent page loads
      localStorage.removeItem('robridge_tour_should_auto_start');
      localStorage.removeItem('robridge_tour_branch');
      
      console.log('⏰ Setting step to 1 synchronously');
      setStep(1);
      localStorage.setItem('robridge_tour_step', '1');
    }
  }, [activeWorkspaceId, step]);

  // Read zone counts and names dynamically from the DOM cards
  const getZoneData = () => {
    const cards = document.querySelectorAll('.grn-card');
    const list = [];
    cards.forEach(card => {
      const nameEl = card.querySelector('.grn-id');
      const name = nameEl ? nameEl.textContent.trim() : '';
      const metaSpans = card.querySelectorAll('.grn-meta span');
      let units = 0;
      if (metaSpans && metaSpans.length > 1) {
        const unitsText = metaSpans[1].textContent;
        units = parseInt(unitsText.replace(/[^0-9]/g, ''), 10) || 0;
      }
      const isActive = card.classList.contains('active');
      list.push({ name, units, isActive });
    });
    return list;
  };

  // Get selector for targeting element at each step
  const getStepSelector = (s) => {
    switch (s) {
      case 1: return '#tour-header';
      case 2: return '#tour-step-scanner';
      case 3: return '#tour-pair-btn';
      case 4: return '.guide-options-grid';
      case 5: return '#tour-sidebar-dashboard';
      case 6: return '#tour-step-scanner';
      case 7: return '#tour-step-catalog';
      case 8: {
        if (document.getElementById('tour-add-product')) return '#tour-add-product';
        if (document.getElementById('tour-add-product-empty')) return '#tour-add-product-empty';
        if (document.getElementById('tour-save-master-btn')) return '#tour-save-master-btn';
        if (document.getElementById('tour-first-master')) return '#tour-first-master';
        return '#tour-create-master-btn';
      }
      case 9: return '#tour-save-product';
      case 10: return '#tour-sidebar-dashboard';
      case 11: return '#tour-header';
      case 12: {
        if (document.getElementById('tour-onboard-submit-btn')) return '#tour-onboard-submit-btn';
        return '#tour-scan-input';
      }
      case 13: return '#tour-sidebar-dashboard';
      case 14: return '#tour-header';
      case 15: return '.wifi-qr-floating-card';
      case 20: {
        const zoneCards = document.querySelectorAll('.grn-card');
        if (zoneCards.length === 0) return '#tour-add-zone-btn';
        return '#tour-zone-cards-list';
      }
      case 21: return '#tour-add-zone-btn';
      case 22: return '#tour-create-zone-submit-btn';
      case 23: return '#tour-zone-cards-list';
      case 24: return '#tour-assign-btn';
      case 25: return '#tour-assign-submit-btn';
      case 26: {
        const zones = getZoneData();
        if (zones.length <= 1) return '#tour-add-zone-btn';
        const activeZone = zones.find(z => z.isActive);
        const otherZone = zones.find(z => !z.isActive);
        if (activeZone && activeZone.units > 0) {
          return '#tour-transfer-out-btn';
        }
        if (activeZone && activeZone.units === 0 && otherZone && otherZone.units > 0) {
          return '#tour-transfer-in-btn';
        }
        if (activeZone && activeZone.units === 0 && (!otherZone || otherZone.units === 0)) {
          return '#tour-assign-btn';
        }
        return '#tour-transfer-in-btn';
      }
      case 27: return '#tour-transfer-submit-btn';
      case 28: return '#tour-sidebar-scanner';
      case 29: return '#tour-scanner-putaway-btn';
      case 30: return '.putaway-location-box';
      case 31: return '#tour-scan-input';
      case 32: return '.found-item-card';
      case 33: return '#tour-zone-detail-panel';
      default: return null;
    }
  };

  // Keep track of target coordinates and apply highlighted class
  useEffect(() => {
    if (step === 0) {
      // Remove class from all elements when tour is inactive
      document.querySelectorAll('.tour-highlighted-element').forEach(el => {
        el.classList.remove('tour-highlighted-element');
      });
      setTargetRect(null);
      return;
    }

    const updatePosition = () => {
      const selector = getStepSelector(step);
      if (!selector) {
        setTargetRect(null);
        return;
      }

      const el = document.querySelector(selector);
      if (el) {
        if (!el.classList.contains('tour-highlighted-element')) {
          // Remove from other elements
          document.querySelectorAll('.tour-highlighted-element').forEach(other => {
            if (other !== el) other.classList.remove('tour-highlighted-element');
          });
          el.classList.add('tour-highlighted-element');
        }
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
          bottom: rect.bottom + window.scrollY,
          right: rect.right + window.scrollX
        });
      } else {
        setTargetRect(null);
      }
    };

    updatePosition();
    const interval = setInterval(updatePosition, 300); // Poll in background to handle page renders
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      document.querySelectorAll('.tour-highlighted-element').forEach(el => {
        el.classList.remove('tour-highlighted-element');
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.pathname]);

  // Handle automatic step progressions based on actions/paths/DOM states
  useEffect(() => {
    if (step === 0) return;

    // Step 2 -> 3: User clicked scanner setup card and navigated to Device Manager
    if (step === 2 && location.pathname === '/device-manager') {
      setStep(3);
      localStorage.setItem('robridge_tour_step', '3');
      return;
    }

    // Step 3 -> 15: Pairing modal opens with QR code
    if (step === 3) {
      if (document.querySelector('.wifi-qr-floating-card')) {
        setStep(15);
        localStorage.setItem('robridge_tour_step', '15');
        return;
      }
      const guideModalBtn = document.querySelector('.btn-continue-pairing');
      if (guideModalBtn) {
        setStep(4);
        localStorage.setItem('robridge_tour_step', '4');
        return;
      }
    }

    // Step 15 -> 3: Pairing modal closes without pairing
    if (step === 15) {
      if (!document.querySelector('.wifi-qr-floating-card')) {
        setStep(3);
        localStorage.setItem('robridge_tour_step', '3');
        return;
      }
      // Step 15 -> 4: Device successfully paired
      const guideModalBtn = document.querySelector('.btn-continue-pairing');
      if (guideModalBtn) {
        setStep(4);
        localStorage.setItem('robridge_tour_step', '4');
        return;
      }
    }

    // Step 4 -> 5, 8, or 12: User selected catalog, scanner, or continued pairing
    if (step === 4) {
      if (location.pathname === '/ims-catalog') {
        setBranch('catalog');
        localStorage.setItem('robridge_tour_branch', 'catalog');
        setStep(8);
        localStorage.setItem('robridge_tour_step', '8');
        return;
      }
      if (location.pathname === '/ims-scanner') {
        setBranch('scanner');
        localStorage.setItem('robridge_tour_branch', 'scanner');
        setStep(12);
        localStorage.setItem('robridge_tour_step', '12');
        return;
      }
      const guideModalBtn = document.querySelector('.btn-continue-pairing');
      if (!guideModalBtn) {
        setStep(5);
        localStorage.setItem('robridge_tour_step', '5');
        return;
      }
    }

    // Step 5 -> 6: User returned to Dashboard
    if (step === 5 && (location.pathname === '/' || location.pathname === '/dashboard')) {
      setStep(6);
      localStorage.setItem('robridge_tour_step', '6');
      return;
    }

    // Step 7 -> 8: User clicked catalog card and navigated to IMS Catalog
    if (step === 7 && location.pathname === '/ims-catalog') {
      setStep(8);
      localStorage.setItem('robridge_tour_step', '8');
      return;
    }

    // Step 8 -> 9: User clicked "Add Product" opening the modal
    if (step === 8) {
      const saveProductBtn = document.querySelector('#tour-save-product');
      if (saveProductBtn) {
        setStep(9);
        localStorage.setItem('robridge_tour_step', '9');
        return;
      }
    }

    // Step 9 -> 10: User filled product info and clicked save (modal closed)
    if (step === 9) {
      const saveProductBtn = document.querySelector('#tour-save-product');
      // We check if the modal has closed. We also check if the catalog table/grid has products
      if (!saveProductBtn) {
        setStep(10);
        localStorage.setItem('robridge_tour_step', '10');
        return;
      }
    }

    // Step 10 -> 11: User returned to Dashboard
    if (step === 10 && (location.pathname === '/' || location.pathname === '/dashboard')) {
      setStep(11);
      localStorage.setItem('robridge_tour_step', '11');
      return;
    }

    // Step 12 -> 13: User performed a scan successfully (no onboarding modal active)
    if (step === 12) {
      const scanStatusIconOk = document.querySelector('.scan-status-icon.ok');
      const onboardingModal = document.getElementById('tour-onboard-submit-btn');
      if (scanStatusIconOk && !onboardingModal) {
        setStep(13);
        localStorage.setItem('robridge_tour_step', '13');
        return;
      }
    }

    // Step 13 -> 14: User returned to Dashboard from scanner
    if (step === 13 && (location.pathname === '/' || location.pathname === '/dashboard')) {
      setStep(14);
      localStorage.setItem('robridge_tour_step', '14');
      return;
    }

    // Step 20/21 -> 22: User clicked Add Zone, modal opened
    if (step === 20 || step === 21) {
      if (document.getElementById('tour-create-zone-submit-btn')) {
        setStep(22);
        localStorage.setItem('robridge_tour_step', '22');
        return;
      }
    }

    // Step 22 -> 23: User created zone and modal closed
    if (step === 22) {
      if (!document.getElementById('tour-create-zone-submit-btn')) {
        setStep(23);
        localStorage.setItem('robridge_tour_step', '23');
        return;
      }
    }

    // Step 24 -> 25: User clicked Assign Items to Zone, modal opened
    if (step === 24) {
      if (document.getElementById('tour-assign-submit-btn')) {
        setStep(25);
        localStorage.setItem('robridge_tour_step', '25');
        return;
      }
    }

    // Step 25 -> 26: User assigned items and modal closed
    if (step === 25) {
      if (!document.getElementById('tour-assign-submit-btn')) {
        setStep(26);
        localStorage.setItem('robridge_tour_step', '26');
        return;
      }
    }

    // Step 26 -> 27 or 22: User clicked transfer or add zone
    if (step === 26) {
      if (document.getElementById('tour-transfer-submit-btn')) {
        setStep(27);
        localStorage.setItem('robridge_tour_step', '27');
        return;
      }
      const zoneCount = document.querySelectorAll('.grn-card').length;
      if (zoneCount <= 1 && document.getElementById('tour-create-zone-submit-btn')) {
        setStep(22);
        localStorage.setItem('robridge_tour_step', '22');
        return;
      }
    }

    // Step 27 -> 33: User submitted transfer and modal closed, show updated stock screen
    if (step === 27) {
      if (!document.getElementById('tour-transfer-submit-btn')) {
        setStep(33);
        localStorage.setItem('robridge_tour_step', '33');
        return;
      }
    }

    // Step 28 -> 29: User navigated to Smart Scanner page
    if (step === 28 && location.pathname === '/ims-scanner') {
      setStep(29);
      localStorage.setItem('robridge_tour_step', '29');
      return;
    }

    // Step 29 -> 30: User switched mode to PUTAWAY (so locations box exists)
    if (step === 29) {
      if (document.querySelector('.putaway-location-box')) {
        setStep(30);
        localStorage.setItem('robridge_tour_step', '30');
        return;
      }
    }

  }, [step, location.pathname, targetRect]);

  // Step 31 → 32: Watch for scan-match box or found-item card appearing after barcode is submitted
  useEffect(() => {
    if (step !== 31) return;

    // Poll every 400ms for the scan match result box or found item card to appear
    const pollInterval = setInterval(() => {
      const matchBox = document.getElementById('tour-scanner-scan-match-box') || document.querySelector('.found-item-card');
      if (matchBox) {
        clearInterval(pollInterval);
        setStep(32);
        localStorage.setItem('robridge_tour_step', '32');
      }
    }, 400);

    // Also advance if user presses Enter in the scan input
    const scanInput = document.getElementById('tour-scan-input');
    const handleEnter = (e) => {
      if (e.key === 'Enter' && e.target.value.trim()) {
        // Poll briefly to wait for DOM update after Enter
        let tries = 0;
        const waitForMatch = setInterval(() => {
          tries++;
          if (document.getElementById('tour-scanner-scan-match-box') || document.querySelector('.found-item-card')) {
            clearInterval(waitForMatch);
            clearInterval(pollInterval);
            setStep(32);
            localStorage.setItem('robridge_tour_step', '32');
          }
          if (tries > 15) clearInterval(waitForMatch); // give up after ~3s
        }, 200);
      }
    };
    if (scanInput) scanInput.addEventListener('keydown', handleEnter);

    return () => {
      clearInterval(pollInterval);
      if (scanInput) scanInput.removeEventListener('keydown', handleEnter);
    };
  }, [step]);

  // Click listener to handle card selection for step 20, 21, and 23
  useEffect(() => {
    if (step === 20 || step === 21 || step === 23) {
      const handleCardClick = (e) => {
        if (e.target.closest('.grn-card')) {
          setStep(24);
          localStorage.setItem('robridge_tour_step', '24');
        }
      };
      document.addEventListener('click', handleCardClick);
      return () => document.removeEventListener('click', handleCardClick);
    }
  }, [step]);

  const handleSkipTour = () => {
    setStep(0);
    setBranch(null);
    localStorage.removeItem('robridge_tour_step');
    localStorage.removeItem('robridge_tour_branch');
    if (activeWorkspaceId) {
      localStorage.setItem(`robridge_tour_completed_${activeWorkspaceId}`, 'true');
    }
  };

  const handleNextStep = () => {
    const next = step + 1;
    setStep(next);
    localStorage.setItem('robridge_tour_step', String(next));
  };

  if (step === 0) return null;

  const tourContent = {
    1: {
      title: 'Welcome to RoBridge!',
      text: 'This is your Command Center dashboard. Let\'s complete your initial workspace setup using this quick interactive guide.',
      buttonText: 'Start Setup',
      action: handleNextStep,
    },
    2: {
      title: 'Connect Scanner',
      text: 'First, let\'s pair a device. Click the highlighted \'Connect Smart Scanner\' card to go to the Device Manager panel.',
      buttonText: null,
    },
    3: {
      title: 'Pair Your Device',
      text: 'Click the highlighted \'Pair New Device\' button to generate a QR pairing code for your BVS Scanner.',
      buttonText: null,
    },
    4: {
      title: 'Pairing Successful!',
      text: 'Your scanner is connected! Choose \'Go to Catalog\' to setup product inventory, or \'Go to Scanner\' to test live scanning.',
      buttonText: null,
    },
    5: {
      title: 'Return to Dashboard',
      text: 'Awesome! Click \'Overview\' in the sidebar menu to return to the Command Center and verify your progress.',
      buttonText: null,
    },
    6: {
      title: 'Connection Completed!',
      text: 'Your smart scanner connection is verified and Step 1 is marked done! Let\'s setup Step 2: Product Catalog.',
      buttonText: 'Next Step',
      action: handleNextStep,
    },
    7: {
      title: 'Add Products',
      text: 'Click the highlighted \'Add Catalog Items\' card to go to the Master Catalog panel.',
      buttonText: null,
    },
    8: (() => {
      if (document.getElementById('tour-add-product')) {
        return {
          title: 'Create Your SKU',
          text: 'Click the highlighted \'Add Product\' button to open the product details modal.',
          buttonText: null
        };
      }
      if (document.getElementById('tour-add-product-empty')) {
        return {
          title: 'Create Your SKU',
          text: 'Click the highlighted \'Add Manually\' button to open the product details modal.',
          buttonText: null
        };
      }
      if (document.getElementById('tour-save-master-btn')) {
        return {
          title: 'Define Catalog Space',
          text: 'Enter a name for your master catalog, then click \'Create Master\' to define it.',
          buttonText: null
        };
      }
      if (document.getElementById('tour-first-master')) {
        return {
          title: 'Select Master Catalog',
          text: 'Select the master catalog you want to manage. Click on the highlighted catalog card to open it.',
          buttonText: null
        };
      }
      return {
        title: 'Create Master Catalog',
        text: 'You don\'t have any master catalogs yet. Click the highlighted \'Create Master Catalog\' button to define a new catalog space.',
        buttonText: null
      };
    })(),
    9: {
      title: 'Save Product SKU',
      text: 'Input a barcode and product name, then click \'Add Product\' to register this item in your inventory catalog.',
      buttonText: null,
    },
    10: {
      title: 'Return to Command Center',
      text: 'Product successfully added! Click \'Overview\' in the sidebar to return to the dashboard and see your live command center.',
      buttonText: null,
    },
    11: {
      title: 'Workspace Live!',
      text: 'Congratulations! Both steps are complete. Your Inventory Health calculation, KPI grids, alerts, and weekly movements charts are active. Access this guide anytime from the \'Restart Guide Tour\' button.',
      buttonText: 'Finish',
      action: handleSkipTour,
    },
    12: (() => {
      if (document.getElementById('tour-onboard-submit-btn')) {
        return {
          title: 'Onboard Unexpected Item',
          text: 'You scanned an unexpected barcode! Enter a product name, category, and catalog placement, then click \'Onboard & Receive\' to register it.',
          buttonText: null,
        };
      }
      return {
        title: 'Test Smart Scanner',
        text: 'This is the live scanning interface. Type a barcode (e.g. \'GEN-123456\') and click \'Scan\' or press Enter to simulate a barcode scan.',
        buttonText: null,
      };
    })(),
    13: {
      title: 'Return to Dashboard',
      text: 'Product registered and logged successfully! Click \'Overview\' in the sidebar menu to return to the Command Center and see your live dashboard statistics.',
      buttonText: null,
    },
    14: {
      title: 'Onboarding Complete!',
      text: 'Congratulations! Your BVS Scanner is paired and verified. Your Command Center live feed and metrics are active.',
      buttonText: 'Finish',
      action: handleSkipTour,
    },
    15: {
      title: 'Scan to Pair Device',
      text: 'Scan this QR code with your Barcode Scanner to pair it with your account.',
      buttonText: null,
    },
    20: (() => {
      const zoneCards = document.querySelectorAll('.grn-card');
      if (zoneCards.length === 0) {
        return {
          title: 'Location & Zone Tracking',
          text: 'This module tracks inventory mapped to physical storage zones. You have no zones yet — click the highlighted \'Add Zone\' button to create your first storage zone.',
          buttonText: null,
        };
      }
      return {
        title: 'Zone Tracking Overview',
        text: 'This dashboard tracks inventory items mapped to physical storage zones in your facility. Click on any zone card in the list to view its stock details, or click Next to add a new zone.',
        buttonText: 'Next',
        action: handleNextStep,
      };
    })(),
    21: {
      title: 'Create Storage Zone',
      text: 'Click the highlighted \'Add Zone\' button at the top right to open the custom zone configuration form.',
      buttonText: null,
    },
    22: {
      title: 'Save Zone Details',
      text: 'Type a unique Zone Name, select its operational type, and click the highlighted \'Create Zone\' button to save this storage zone.',
      buttonText: null,
    },
    23: {
      title: 'Select Created Zone',
      text: 'Your new zone has been created successfully! Now, click on the newly created card in the list to select it and view its details.',
      buttonText: null,
    },
    24: {
      title: 'Assign Products to Zone',
      text: 'To add stock to this zone, click the highlighted \'Assign Items to Zone\' button. If this zone already has stock, you can skip to transfers by clicking the button below.',
      buttonText: 'Skip to Transfer',
      action: () => {
        setStep(26);
        localStorage.setItem('robridge_tour_step', '26');
      }
    },
    25: {
      title: 'Confirm Stock Assignment',
      text: 'Check off the items you want to place in this zone, enter their quantities, and click the highlighted \'Assign Items\' button to save.',
      buttonText: null,
    },
    26: (() => {
      const zones = getZoneData();
      if (zones.length <= 1) {
        return {
          title: 'Create Secondary Zone',
          text: 'Stock transfers require at least two zones. Click the highlighted \'Add Zone\' button to define a second destination zone, or click Skip below to bypass the transfer flow.',
          buttonText: 'Skip Transfer',
          action: () => {
            setStep(28);
            localStorage.setItem('robridge_tour_step', '28');
          }
        };
      }
      const activeZone = zones.find(z => z.isActive);
      const otherZone = zones.find(z => !z.isActive);

      if (activeZone && activeZone.units > 0) {
        return {
          title: 'Transfer Stock Out',
          text: `${activeZone.name} has ${activeZone.units} units, while ${otherZone ? otherZone.name : 'the other zone'} has ${otherZone ? otherZone.units : 0} units. Let's transfer stock out of ${activeZone.name} by clicking 'Transfer Stock Out'.`,
          buttonText: null,
        };
      }
      if (activeZone && activeZone.units === 0 && otherZone && otherZone.units > 0) {
        return {
          title: 'Transfer Stock In',
          text: `${activeZone.name} is empty, but ${otherZone.name} has ${otherZone.units} units. Let's pull stock into ${activeZone.name} by clicking 'Transfer Stock In'.`,
          buttonText: null,
        };
      }
      return {
        title: 'Assign Stock First',
        text: `Both zones are empty! We cannot perform a transfer yet. Click 'Assign Items to Zone' first to add stock to ${activeZone ? activeZone.name : 'your active zone'}.`,
        buttonText: null,
      };
    })(),
    27: {
      title: 'Execute Stock Transfer',
      text: 'Select the origin zone, input the barcode/quantity, and click the highlighted \'Transfer\' button to finalize the stock movement.',
      buttonText: null,
    },
    28: {
      title: 'Go to Smart Scanner',
      text: 'Great! Now click the highlighted \'Smart Scanner\' tab in the sidebar menu (or click the button below) to view scanner-based Put Away.',
      buttonText: 'Go to Scanner',
      action: () => {
        navigate('/ims-scanner');
        setStep(29);
        localStorage.setItem('robridge_tour_step', '29');
      }
    },
    29: {
      title: 'Select Put Away Mode',
      text: 'Let\'s configure the scanner for stocking. Click the highlighted \'PUTAWAY\' button in the top tab bar to switch operation modes.',
      buttonText: null,
    },
    30: {
      title: 'Configure Target Locations',
      text: 'Set up the route coordinates: select the Source location (From) to pull stock from, and the Target location (To) to put the items into. Click Next to proceed.',
      buttonText: 'Next',
      action: () => {
        setStep(31);
        localStorage.setItem('robridge_tour_step', '31');
      }
    },
    31: {
      title: 'Scan Barcode or Enter Manually',
      text: 'Scan the item\'s barcode with your device scanner, or manually type the product barcode value into the highlighted input field and press Enter.',
      buttonText: null,
    },
    32: {
      title: 'Verify Put Away Confirmation',
      text: 'The barcode scan succeeded! Verify the matched item details, quantity, and target zone location. The rack coordinates are now recorded.',
      buttonText: 'Finish Tour',
      action: handleSkipTour,
    },
    33: {
      title: 'Stock Transfer Complete!',
      text: 'The stock movement was successful! Both the left zone list and the details table are highlighted. You can click on either zone card in the list to compare their updated stock balances. Click Next when you are ready to proceed to the Smart Scanner.',
      buttonText: 'Next',
      action: () => {
        setStep(28);
        localStorage.setItem('robridge_tour_step', '28');
      }
    },
  }[step];

  if (!tourContent) return null;

  // Compute placement and offsets
  let top = '50%';
  let left = '50%';
  let placement = 'bottom';
  let arrowLeft = 142; // Default centered (150px - 8px)
  let arrowTop = null;

  if (targetRect) {
    if (step === 5 || step === 10 || step === 13 || step === 28) {
      // Sidebar - place to the right of Dashboard/Scanner item
      top = `${targetRect.top + (targetRect.height / 2) - 30}px`;
      left = `${targetRect.right + 15}px`;
      placement = 'right';
    } else if (step === 20 && document.querySelectorAll('.grn-card').length === 0) {
      // No zones: Place the guide card directly below the Add Zone button on the right
      const tooltipWidth = 320;
      top = `${targetRect.bottom + 15}px`;
      const proposedLeft = targetRect.right - tooltipWidth;
      const clampedLeft = Math.max(15, Math.min(proposedLeft, window.innerWidth - tooltipWidth - 15));
      left = `${clampedLeft}px`;
      placement = 'bottom'; // Arrow points UP from top of tooltip card

      const targetCenterX = targetRect.left + (targetRect.width / 2);
      arrowLeft = Math.max(15, Math.min(targetCenterX - clampedLeft - 8, tooltipWidth - 25));
    } else if (step === 24 || step === 32 || (step === 26 && getStepSelector(26) !== '#tour-add-zone-btn')) {
      // Place to the left of the target element to avoid covering contents
      const targetCenterY = targetRect.top + (targetRect.height / 2);
      let calculatedTop = targetCenterY - 80;
      const tooltipHeight = 200; // estimated card height
      const padding = 15;
      const maxTop = window.scrollY + window.innerHeight - tooltipHeight - padding;
      const minTop = window.scrollY + padding;
      const clampedTop = Math.max(minTop, Math.min(calculatedTop, maxTop));

      top = `${clampedTop}px`;
      left = `${targetRect.left - 320 - 15}px`;
      placement = 'left';
      arrowTop = targetCenterY - clampedTop;
    } else if (step === 33) {
      // Center tooltip above the right zone detail panel, pointing DOWN toward it
      const tooltipWidth = 320;
      const tooltipCenterX = targetRect.left + (targetRect.width / 2);
      const proposedLeft = tooltipCenterX - tooltipWidth / 2;
      const clampedLeft = Math.max(15, Math.min(proposedLeft, window.innerWidth - tooltipWidth - 15));
      top = `${window.scrollY + 20}px`; // Just below the page header bar
      left = `${clampedLeft}px`;
      placement = 'top'; // Arrow at bottom of card, pointing DOWN at the detail panel
      // Align arrow toward the horizontal center of the detail panel
      arrowLeft = Math.max(15, Math.min(tooltipCenterX - clampedLeft - 8, tooltipWidth - 25));
    } else if (step === 4) {
      // Success Modal - place to the right of the guide modal card itself to avoid hiding contents
      const modalEl = document.querySelector('.guide-modal-card');
      if (modalEl) {
        const modalRect = modalEl.getBoundingClientRect();
        top = `${modalRect.top + window.scrollY + (modalRect.height / 2) - 20}px`;
        const minPadding = 15;
        let targetLeft = modalRect.right + 25;
        const maxLeft = window.innerWidth - 320 - minPadding;
        left = `${Math.min(targetLeft, maxLeft)}px`;
        placement = 'right';
      } else {
        // Fallback: place below target
        top = `${targetRect.bottom + 15}px`;
        left = `${targetRect.left + (targetRect.width / 2) - 150}px`;
        placement = 'bottom';
      }
    } else if (document.querySelector('.ims-modal')) {
      // Catalog/Product Dialog - place to the right of the dialog to avoid blocking form fields
      const modalEl = document.querySelector('.ims-modal');
      const modalRect = modalEl.getBoundingClientRect();
      top = `${modalRect.top + window.scrollY + (modalRect.height / 2) - 100}px`;
      const minPadding = 15;
      let targetLeft = modalRect.right + 25;
      const maxLeft = window.innerWidth - 320 - minPadding;
      left = `${Math.min(targetLeft, maxLeft)}px`;
      placement = 'right';
    } else {
      // Constrain horizontal bounds
      const minPadding = 15;
      let targetLeft = targetRect.left + (targetRect.width / 2) - 150;
      const maxLeft = window.innerWidth - 320 - minPadding;
      const minLeft = minPadding;
      targetLeft = Math.max(minLeft, Math.min(targetLeft, maxLeft));
      left = `${targetLeft}px`;

      // Calculate where the arrow should be relative to the tooltip card
      const targetCenter = targetRect.left + (targetRect.width / 2);
      arrowLeft = targetCenter - targetLeft - 8;
      // Keep arrow within card bounds (min 15px, max 297px)
      arrowLeft = Math.max(15, Math.min(arrowLeft, 297));

      // Calculate vertical space constraints to choose between bottom and top placement
      const tooltipHeight = 220; // safe estimation to prevent clipping
      const viewportBottom = targetRect.bottom - window.scrollY;

      if (step === 15) {
        // Force bottom placement for the QR code card so the guide box is below it
        top = `${targetRect.bottom + 15}px`;
        placement = 'bottom';
      } else if (viewportBottom + tooltipHeight + minPadding > window.innerHeight) {
        // Not enough space below the target inside current viewport, place it above!
        top = `${targetRect.top - tooltipHeight - 15}px`;
        placement = 'top';
      } else {
        // Enough space below target, default to bottom
        top = `${targetRect.bottom + 15}px`;
        placement = 'bottom';
      }
    }
  }

  return (
    <>
      <div className="tour-overlay-backdrop" onClick={handleSkipTour} />

      <div
        className={`tour-tooltip-card placement-${placement}`}
        style={{
          position: 'absolute',
          top: top,
          left: left,
          zIndex: 10010,
          width: '320px',
          background: '#ffffff',
          borderRadius: '12px',
          border: '1.5px solid #E3821E',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '16px',
          color: '#202124',
          transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
          '--arrow-left': `${arrowLeft}px`,
          '--arrow-top': arrowTop !== null ? `${arrowTop}px` : undefined,
        }}
      >
        <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#202124', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FaCompass style={{ color: '#E3821E' }} /> {tourContent.title}
        </h3>
        <p style={{ fontSize: '13px', color: '#5f6368', lineHeight: '1.5', marginBottom: '16px' }}>
          {tourContent.text}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={handleSkipTour}
            style={{
              background: 'none',
              border: 'none',
              color: '#9aa0a6',
              fontSize: '12px',
              cursor: 'pointer',
              padding: 0,
              fontWeight: '500',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#5f6368'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9aa0a6'}
          >
            Skip Tour
          </button>

          {tourContent.buttonText && (
            <button
              onClick={tourContent.action}
              className="btn btn-primary"
              style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '6px' }}
            >
              {tourContent.buttonText}
            </button>
          )}
        </div>

        {/* Progress Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '14px' }}>
          {(branch === 'zone' ? [20, 21, 22, 23, 24, 25, 26, 27, 28] : (branch === 'scanner' ? [1, 2, 3, 4, 12, 13, 14] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11])).map((s) => (
            <div
              key={s}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: (s === step || (s === 3 && step === 15)) ? '#E3821E' : '#e8eaed',
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default TourGuide;
